import { Link } from "react-router-dom";
import {
  Atom,
  Droplets,
  FlaskConical,
  Leaf,
  ArrowUpRight,
} from "lucide-react";
import Section from "@/components/Section";
import { Button } from "@/components/ui/button";

const areas = [
  {
    icon: Atom,
    title: "Protein Powders",
    tag: "01 · Plant & Dairy Systems",
    points: [
      "Bitterness suppression in pea, soy, rice, and fava isolates",
      "Off note reduction in whey and collagen blends",
      "Mouthfeel enhancement for low fat, high protein matrices",
      "Sweetness lift for reduced sugar and stevia forward sweetening",
    ],
    image:
      "https://static.prod-images.emergentagent.com/jobs/d836de25-6805-483e-b4de-64a38c2f97c1/images/7de8a93832426a3d350d20c819051e4e2409f957ae619d08ac944217b00143ae.png",
  },
  {
    icon: Droplets,
    title: "Hydration & Electrolytes",
    tag: "02 · Mineral Forward Matrices",
    points: [
      "Smoothing of saline/metallic edges in sodium and potassium blends",
      "Magnesium bitterness suppression",
      "Sweetness enhancement for low calorie hydration",
      "Clean delivery in rapid dissolve powder sticks and RTD formats",
    ],
    image:
      "https://static.prod-images.emergentagent.com/jobs/d836de25-6805-483e-b4de-64a38c2f97c1/images/afa6c3c8bf68c93b6d54693b1c19c17a0b2bb0b1494e7303f6cfaac5e32e0a64.png",
  },
  {
    icon: FlaskConical,
    title: "Functional Beverages",
    tag: "03 · RTD & Concentrate",
    points: [
      "Caffeine and high actives bitterness modulation",
      "Balancing of botanicals in modern energy and nootropic formats",
      "Stability aware flavor and modulator systems for hot fill and cold fill",
      "Sugar reduction programs without loss of perceived richness",
    ],
    image:
      "https://images.unsplash.com/photo-1628863353691-0071c8c1874c",
  },
  {
    icon: Leaf,
    title: "Nootropics & Adaptogens",
    tag: "04 · Botanical & Functional Extracts",
    points: [
      "Mushroom extract modulation (lion's mane, reishi, cordyceps)",
      "Adaptogen bitterness/astringency control (ashwagandha, rhodiola)",
      "Masking for amino acid and peptide actives",
      "Mouthfeel and finish work for high extract loadings",
    ],
    image:
      "https://static.prod-images.emergentagent.com/jobs/d836de25-6805-483e-b4de-64a38c2f97c1/images/1442fbe8bdc46b2c825ae0ce3950c6c9207b8e156dffb30ae614c594a2830fc1.png",
  },
];

export default function Applications() {
  return (
    <div className="bg-white">
      <section className="pt-32 md:pt-40 pb-16 border-b border-gray-100" data-testid="applications-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <span className="pm-overline">Applications</span>
            <h1 className="mt-5 font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-gray-900 leading-[1.04]">
              Categories we formulate into. Every day.
            </h1>
          </div>
          <div className="lg:col-span-4 text-sm text-gray-600 leading-relaxed">
            From actives heavy protein systems to mineral forward hydration
            sticks, our modulation platforms are tuned to the matrices driving
            modern functional nutrition.
          </div>
        </div>
      </section>

      {areas.map((area, i) => {
        const Icon = area.icon;
        const reverse = i % 2 === 1;
        return (
          <Section
            key={area.title}
            testId={`application-detail-${i}`}
            bg={i % 2 === 0 ? "white" : "muted"}
          >
            <div
              className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                reverse ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              <div className="lg:col-span-6">
                <div className="aspect-[4/3] rounded-sm overflow-hidden border border-gray-200 bg-white">
                  <img
                    src={area.image}
                    alt={area.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="lg:col-span-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-sm bg-brand-subtle flex items-center justify-center text-brand">
                    <Icon size={20} strokeWidth={1.6} />
                  </div>
                  <span className="text-[10px] tracking-[0.2em] uppercase text-gray-500 font-medium">
                    {area.tag}
                  </span>
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-gray-900 leading-tight">
                  {area.title}
                </h2>
                <ul className="mt-8 border-t border-gray-200">
                  {area.points.map((pt, pi) => (
                    <li
                      key={pt}
                      data-testid={`application-point-${i}-${pi}`}
                      className="flex items-start gap-4 py-4 border-b border-gray-200 text-sm text-gray-700"
                    >
                      <span className="pm-num text-xs text-brand pt-0.5 w-8 shrink-0">
                        0{pi + 1}
                      </span>
                      <span className="leading-relaxed">{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Section>
        );
      })}

      <Section testId="applications-cta" bg="white">
        <div className="border-t border-gray-200 pt-14 flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
          <div>
            <span className="pm-overline">Not Listed?</span>
            <h2 className="mt-3 font-heading text-2xl md:text-3xl font-semibold text-gray-900 max-w-xl leading-tight">
              We frequently formulate into adjacent and emerging categories.
              Tell us about yours.
            </h2>
          </div>
          <Button
            asChild
            data-testid="applications-cta-contact"
            className="bg-brand hover:bg-brand-hover text-white rounded-sm h-12 px-7 text-[15px] font-medium"
          >
            <Link to="/contact">
              Contact Our Team <ArrowUpRight className="ml-2" size={16} />
            </Link>
          </Button>
        </div>
      </Section>
    </div>
  );
}
